// WP-5.1 (RE-2, Snapshot, S-27/28): position-over-time — the RE-2 chart+table Reports.tsx's
// own header comment named as "needs P5 entities (Snapshots) that don't exist yet." A
// dedicated page rather than folding into ReportDetail.tsx's generic ReportFamily dispatcher:
// that dispatcher is a PURE, DB-free `queryReport` call (packages/engine), whereas a Snapshot
// is a persisted Postgres row (a derived, recomputable aggregate, not a financial fact the
// owner performed) — mixing an async DB-backed series into the engine's own synchronous
// aggregation would break that layer's own architecture.
import type { CurrencyCode } from '@pfos/engine';
import type { SnapshotRow } from '@pfos/shared';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { ghostButtonInlineClass, selectClass } from '../components/FormKit';
import { useRunSnapshotNow, useSnapshotSeries } from '../lib/snapshots';

const CURRENCIES: CurrencyCode[] = ['PKR', 'USD', 'EUR', 'GBP'];

function Chart({ rows }: { rows: SnapshotRow[] }): React.JSX.Element | null {
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map((r) => Math.abs(r.ownFundsMinor)), 1);
  return (
    <div className="mb-4 space-y-1">
      {rows.map((row) => {
        const widthPercent = (Math.abs(row.ownFundsMinor) / max) * 100;
        return (
          <div key={row.date} className="flex items-center gap-2">
            <span className="w-24 shrink-0 truncate text-xs text-muted">{row.date}</span>
            <div className="h-3 flex-1 rounded bg-bg">
              <div
                className={`h-3 rounded ${row.ownFundsMinor < 0 ? 'bg-danger' : 'bg-primary'}`}
                style={{ width: `${String(widthPercent)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function PositionOverTimePage(): React.JSX.Element {
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const { data, isLoading } = useSnapshotSeries({ currency });
  const runNow = useRunSnapshotNow();

  return (
    <div>
      <Link
        to="/reports"
        className="mb-2 flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft size={14} /> Reports
      </Link>
      <h1 className="mb-4 text-xl font-semibold text-ink">Position over time</h1>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-sm text-muted">Currency</span>
          <select
            className={`${selectClass} w-auto`}
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value as CurrencyCode);
            }}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={ghostButtonInlineClass}
          disabled={runNow.isPending}
          onClick={() => {
            runNow.mutate();
          }}
        >
          <RefreshCw size={14} className="mr-1 inline" /> Refresh now
        </button>
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {data !== undefined &&
        (data.rows.length === 0 ? (
          <p className="text-sm text-muted">
            No snapshots yet — daily valuations accumulate once the nightly job runs (or click
            Refresh now).
          </p>
        ) : (
          <>
            <Chart rows={data.rows} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted">
                    <th className="py-2 pr-4 font-medium">Date</th>
                    <th className="py-2 pr-4 font-medium">Own funds</th>
                    <th className="py-2 pr-4 font-medium">Reserves</th>
                    <th className="py-2 pr-4 font-medium">TPF held</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.date} className="border-b border-border last:border-0">
                      <td className="py-2 pr-4 text-ink">{row.date}</td>
                      <td className="py-2 pr-4 text-ink">
                        <Amount minorUnits={row.ownFundsMinor} currency={data.currency} />
                      </td>
                      <td className="py-2 pr-4 text-ink">
                        <Amount minorUnits={row.reservesMinor} currency={data.currency} />
                      </td>
                      <td className="py-2 pr-4 text-ink">
                        <Amount minorUnits={row.tpfHeldMinor} currency={data.currency} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ))}
    </div>
  );
}
